import React, { useEffect, useState } from 'react';
import {
    Box,
    Divider,
    IconButton,
    Table as MuiTable,
    TablePagination,
    Typography,
} from '@material-ui/core';
import styled from 'styled-components';
import CancelIcon from '@material-ui/icons/EventBusy';
import { useTranslation } from 'react-i18next';

import { THeadCell, TTableRow } from 'common/types/table';
import { getDefaultHeadCell } from 'common/utils';
import { useTable } from 'common/hooks';
import { ITableSettings } from 'common/interfaces/tables';
import { Tooltip } from 'common/components/Tooltip';

import { Loader } from '../Loader';
import { ConfirmDialog } from '../ConfirmDialog';

import { TableHead } from './TableHead';
import { TableBody } from './TableBody';

interface IProps {
    className?: string;
    disableOrderBy?: boolean;
    editable?: boolean;
    loading?: boolean;
    tableHeadCells: THeadCell[];
    tableRows: TTableRow[];
    tableSettings?: Pick<ITableSettings, 'rowsPerPage'>;
    totalCount: number;
    onChangePage?(page: number, rowsPerPage: number): void;
    onChangeRowsPerPage?(rowsPerPage: number): void;
    onInvitationCancel?(ids: string[]): void;
    onRowClick?(rowId: string): void;
    onSort?(order: string, headCellId: string): void;
}

const BaseTable: React.FC<IProps> = ({
    className,
    disableOrderBy,
    editable,
    loading,
    tableHeadCells,
    tableRows,
    tableSettings,
    totalCount,
    onChangePage,
    onChangeRowsPerPage,
    onRowClick,
    onInvitationCancel,
    onSort,
}: IProps) => {
    const { t } = useTranslation();
    const [showCancelInvitationDialog, setShowCancelInvitationDialog] =
        useState(false);

    const defaultHeadCell = getDefaultHeadCell(tableHeadCells);

    const {
        orderBy,
        order,
        selectedRows,
        selectedRowAmount,
        page,
        rowsPerPage,
        setOrder,
        setPage,
        setRowsPerPage,
        handleSelectAllClick,
        handleCheckboxClick,
        isRowSelected,
    } = useTable(tableSettings);

    useEffect(() => {
        if (orderBy) return;

        setOrder(defaultHeadCell.defaultOrder || 'asc', defaultHeadCell.id);
    }, [orderBy]);

    const handleSort = (headCellId: string, disableOrder?: boolean) => {
        if ((disableOrder || disableOrderBy) && orderBy !== headCellId) return;

        const isAsc = orderBy === headCellId && order === 'asc';
        const orderDirection = isAsc ? 'desc' : 'asc';

        setOrder(orderDirection, headCellId);

        onSort && onSort(orderDirection, headCellId);
    };

    const handleInvitationCancel = () => {
        setShowCancelInvitationDialog(false);

        onInvitationCancel && onInvitationCancel(selectedRows);
    };

    const handleChangePage = (page: number) => {
        setPage(page);

        onChangePage && onChangePage(page, rowsPerPage);
    };

    const handleChangeRowsPerPage = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const rowsPerPage = parseInt(e.target.value);

        setRowsPerPage(rowsPerPage);

        onChangeRowsPerPage && onChangeRowsPerPage(rowsPerPage);
    };

    const multipleSelected = selectedRowAmount > 1;

    const cancelConfirmationMessage = multipleSelected
        ? t('dialogs.cancelInvite.titlePlural1') +
          selectedRowAmount +
          t('dialogs.cancelInvite.titlePlural2')
        : t('dialogs.cancelInvite.titleSingle');
    const cancelInformationMessage = multipleSelected
        ? t('dialogs.cancelInvite.messagePlural')
        : t('dialogs.cancelInvite.messageSingle');

    return (
        <Box className={className}>
            <ConfirmDialog
                cancelProps={{
                    onClick: () => setShowCancelInvitationDialog(false),
                }}
                confirmProps={{ onClick: handleInvitationCancel }}
                open={showCancelInvitationDialog}
                title={cancelConfirmationMessage}
            >
                {cancelInformationMessage}
            </ConfirmDialog>
            {editable && (
                <Box
                    display="flex"
                    height="25px"
                    justifyContent="flex-end"
                    my={6}
                    width="100%"
                >
                    <Tooltip title={t('employee.cancelInviteTooltip') || ''}>
                        <IconButton
                            disableRipple
                            className="bulkActionButton"
                            disabled={selectedRowAmount === 0}
                            onClick={() => setShowCancelInvitationDialog(true)}
                        >
                            <CancelIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            <MuiTable component="div">
                <TableHead
                    disableOrderBy={disableOrderBy}
                    editable={editable}
                    order={order}
                    orderBy={orderBy}
                    rowCount={tableRows.length}
                    selectedRowAmount={selectedRowAmount}
                    tableHeadCells={tableHeadCells}
                    onSelectAllClick={(e) => handleSelectAllClick(e, tableRows)}
                    onSort={handleSort}
                />
                <TableBody
                    editable={editable}
                    isRowSelected={isRowSelected}
                    tableRows={tableRows}
                    onCheckboxClick={editable ? handleCheckboxClick : undefined}
                    onRowClick={onRowClick}
                />
            </MuiTable>
            {loading && (
                <Box
                    alignItems="center"
                    display="flex"
                    height="100%"
                    justifyContent="center"
                    mb={6}
                    mt={6}
                >
                    <Loader />
                </Box>
            )}
            {!loading && !tableRows.length && (
                <Box mb={6} mt={6} textAlign="center">
                    <Typography color="textSecondary">
                        {t('employee.noResults')}
                    </Typography>
                </Box>
            )}

            <Box mt="-1px">
                <Divider />
            </Box>

            {editable && (
                <TablePagination
                    className="tablePagination"
                    component="div"
                    count={totalCount}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('employee.of')} ${
                            count !== -1 ? count : 'more than ' + to
                        }`
                    }
                    labelRowsPerPage={t('employee.rowsPerPage')}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 100]}
                    onChangePage={(_e, page) => handleChangePage(page)}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            )}
        </Box>
    );
};

export const Table = styled(BaseTable)`
    overflow-x: auto;

    .bulkActionButton {
        color: ${({ theme }) => theme.palette.primary.main};

        :disabled {
            color: ${({ theme }) => theme.palette.grey[200]};
        }
        .MuiSvgIcon-root {
            font-size: 2rem;
        }
    }

    .tablePagination {
        color: ${({ theme }) => theme.palette.primary.main};

        .MuiTablePagination-caption {
            font-size: ${({ theme }) => theme.typography.body1.fontSize};
        }

        .MuiSelect-selectMenu {
            position: relative;
            top: 2px;
            min-height: auto;
            font-size: ${({ theme }) => theme.typography.body1.fontSize};
        }

        .MuiSelect-icon {
            top: calc(50% - 11px);
            font-size: 1.375rem;
            color: ${({ theme }) => theme.palette.primary.main};
        }

        .MuiTablePagination-actions .MuiSvgIcon-root {
            font-size: 1.375rem;
        }
    }
`;
